import config from "../config";
import { getValidToken } from "../services/tokenRefreshService";

/**
 * The three printable order documents, each of which the API renders two ways:
 * as a page (the default) or as a PDF (`Accept: application/octet-stream`).
 *
 * Printing goes through the page: the counter prints these all day, and a PDF
 * download means a trip through the Downloads folder and a PDF reader for
 * every ticket. The PDF is kept for emailing or filing a copy.
 */
export type OrderDocument = "slip" | "label" | "uberLabel";

/**
 * ⚠️ `slip` and `label` take a Deeliver order id, `uberLabel` an UBER order id
 * — they are different tables. Handing a Deeliver id to the Uber route 404s,
 * and before this module the helpers saved that error body as a ".pdf".
 */
const PATHS: Record<OrderDocument, (id: string) => string> = {
  slip: (id) => `/admin/orders/${id}/picking-slip`,
  label: (id) => `/admin/orders/${id}/packing-slip`,
  uberLabel: (id) => `/admin/uber-orders/${id}/label`,
};

const LABELS: Record<OrderDocument, string> = {
  slip: "order slip",
  label: "label",
  uberLabel: "Uber Eats label",
};

/**
 * Print as soon as the artwork and the fonts are in. Without the wait the
 * page can print with the logos missing and the text in a fallback face,
 * which re-wraps every line. Injected into the document we just fetched
 * rather than baked into the server's view, which is also used for preview.
 */
const PRINT_HOOK = `<script>
  window.addEventListener('load', function () {
    var imgs = Array.prototype.slice.call(document.images);
    Promise.all(imgs.map(function (i) {
      return i.complete ? null : new Promise(function (r) { i.onload = i.onerror = r; });
    }))
      .then(function () { return document.fonts ? document.fonts.ready : null; })
      .then(function () {
        setTimeout(function () {
          window.focus();
          window.print();
        }, 120);
      });
  });
  // the dialog is dismissed (printed or cancelled) — do not leave the tab behind
  window.addEventListener('afterprint', function () { window.close(); });
<\/script>`;

async function fetchDocument(doc: OrderDocument, id: string, accept: string): Promise<Response> {
  const token = await getValidToken();
  const res = await fetch(`${config.API_URL}${PATHS[doc](id)}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, Accept: accept },
  });
  if (!res.ok) {
    // The API answers a failure with JSON. The old helpers wrote that body
    // straight to disk as a .pdf, so a 404 arrived as a "corrupt" file.
    let detail = "";
    try {
      detail = (await res.json())?.message ?? "";
    } catch {
      /* not JSON — the status is all we have */
    }
    throw new Error(`Could not fetch the ${LABELS[doc]} (${res.status})${detail ? `: ${detail}` : ""}`);
  }
  return res;
}

/** Open the document in a window and print it. Never touches the filesystem. */
export async function printDocument(doc: OrderDocument, id: string): Promise<void> {
  const html = await (await fetchDocument(doc, id, "text/html")).text();
  const w = window.open("", "_blank", "width=480,height=860");
  if (!w) throw new Error("The print window was blocked — allow pop-ups for the dashboard.");
  w.document.write(html.replace("</body>", `${PRINT_HOOK}</body>`));
  w.document.close();
}

/** Save the document as a PDF — for emailing or filing, not for the counter. */
export async function downloadDocument(doc: OrderDocument, id: string, filename: string): Promise<void> {
  const blob = await (await fetchDocument(doc, id, "application/octet-stream")).blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Run one of the above and report a failure instead of swallowing it — the
 * whole point of this module is that a print that did not happen says so.
 */
export function runDocumentAction(action: Promise<void>, onError?: (message: string) => void): void {
  action.catch((err: unknown) => {
    const message = err instanceof Error ? err.message : "The document could not be printed.";
    if (onError) onError(message);
    else window.alert(message);
  });
}
